/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import React, { Component } from 'react';
import TagManager from 'react-gtm-module'
// pages
import UIKitPage from 'pages/uikit/uikit_container';
import Error from 'pages/error/error_container';
import Auth from 'pages/auth/auth_container';
import Dashboard from 'pages/dashboard/dashboard_container';
// structure
import Modal from 'material/structure/modal/modal_container';
import MessageHolder from 'material/structure/messageholder/messageholder_container';
import OnInActivityMessage from 'material/message/popup/inactive/inactive';

import LoadState from 'material/states/load/load';

// utils
import { history } from 'utils/history';
import { pageurl, device } from 'utils/environment';
import { language, languages, cookieEnabled, userAgent, visitorGeo, isMobile } from 'utils/user_data';
import { isEmptyObject } from 'utils/isemptyobject';
import { environment } from 'utils/environment';
import { clearAutoLogoutTimer, clearCheckInactivityTimer, setAutoLogoutTimer, setCheckInactivityTimer } from 'utils/auth/manageTimers';
// libraries
import { Switch, Route, Redirect } from 'react-router';
import {ConnectedRouter} from 'react-router-redux';
import 'react-tippy/dist/tippy.css'; // used for tooltip
// styles
import injectSheet from 'react-jss';
import styles from 'app_styles';


/* ========== ~~~~~~~~~~ APP : CHILD ~~~~~~~~~~ ========== */
// PRESENTATIONAL COMPONENT
// receives app state & actions as props from parent "_container" component
// this child component handles local state and uses props passed from parent to read/write application state when necessary
class App extends Component {

    constructor(props) {
        super(props);
        this.state = { phase: 'loading' };
        this.authRef = this.props.queryParams['auth_ref'];
        this.tokenUUID = !!this.authRef && !!this.props.queryParams['token_uuid'] ? this.props.queryParams['token_uuid'] : null;
        this.tagManagerArgs = { gtmId: environment === 'PRODUCTION' ? 'GTM-P972HDC' : 'GTM-T77J246' };
        this.handleSessionExtensionCallback = this.handleSessionExtensionCallback.bind(this);
        this.handleAutoLogout = this.handleAutoLogout.bind(this);
    }
    componentDidMount() {
        this.ensurePermissionsAndSitesList();
        this.props.getUserIP();
        this.props.setSessionData({ language, languages, cookieEnabled, userAgent, pageurl, device, visitorGeo, isMobile });

        // Called every time user reloads the application (this causes session extension in BE). This will extend the session timeout check for 23 hours and 30 mins.
        if(!!this.props.isSignedIn) this.setSessionExtensionTimeout(84600000);

        if(!!this.tokenUUID) this.props.setAuthTokenData({ tokenUUID: this.tokenUUID });

        // change class for intro animation (header/sidebar)
        setTimeout(() => { this.setState({ phase: 'loaded'}); }, 100);
        
        // Initialize Google Tag Manager
        if (environment !== 'LOCALHOST') TagManager.initialize(this.tagManagerArgs)
    }

    setSessionExtensionTimeout(timeout) {
        clearCheckInactivityTimer();
        setCheckInactivityTimer(this.handleSessionExtensionCallback, timeout);
    }

    componentDidUpdate(prevProps){
        if(history.location.pathname.replace('/','') !== this.props.currentPage){
            this.props.updatePageDetails(history.location.pathname.replace('/',''))
        }
        if (prevProps.permissions.length != this.props.permissions.length){
            this.setPaymentInformation();
        }
        // call the function on signin
        if(!!this.props.isSignedIn && prevProps.isSignedIn !== this.props.isSignedIn) this.setSessionExtensionTimeout(84600000);
    }

    // Gets called when user is signed in and the session is about to expire.
    handleSessionExtensionCallback() {
        // get current timestamp
        const currentTime = new Date().getTime();
        // gets the timestamp for last action triggered by user.
        const lastActionDispatchedAt = this.props.lastActionDispatchedAt;
        // calculates time difference between current time and the last time user triggered some action.
        const minutesTolastActionDispatchedAt = Math.ceil(((currentTime - lastActionDispatchedAt) / 1000) / 60);

        // if last time user was active is 15 minutes or more than 15 minutes
        if(minutesTolastActionDispatchedAt >= 15) {
            // auto logout in 30 minutes if no response from user
            setAutoLogoutTimer(this.handleAutoLogout, 1800000);
            // show the modal to confirm if user is still there for 30 minutes
            this.props.displayModal(true, <OnInActivityMessage primaryAction={() => {this.props.extendSession().then(() => this.handleExtendSessionSuccess()).catch(() => this.handleExtendSessionFailure());}}/>, false);
        } else {
            // if the user is active/was recently active on the browser, auto extend the session by 24 hours more. Reset the session extension timeout.
            this.props.extendSession().then(() => this.handleExtendSessionSuccess()).catch(() => this.handleExtendSessionFailure());

        }
    }

    handleAutoLogout() {
        if(!!this.props.mocked) {
            this.props.unmockUser();
        }
        else {
            this.props.signOut();
        }
    }

    handleExtendSessionSuccess() {
        clearAutoLogoutTimer();
        this.setSessionExtensionTimeout(84600000);
        this.props.displayModal(false);
    };

    handleExtendSessionFailure() {
        this.props.displayErrorMessage('Sorry! we were unable to extend your session.');
        this.props.displayModal(false);
    }

    userIsSignedIn = () => this.props.isSignedIn;
    authFail = () => this.props.authFail;
    userDoesNotHavePermissions = () => this.props.permissions.length < 1;

    ensurePermissionsAndSitesList() {
        if (this.userIsSignedIn() && this.userDoesNotHavePermissions()) this.props.getUserPermissions(this.props.userUUID);
        if (this.userIsSignedIn() && isEmptyObject(this.props.sites)) this.props.getAllSites(this.props.accountUUID);
        // if (this.props.isSignedIn && this.props.permissions.length < 1) this.props.getUserPermissions(this.props.userUUID);
        // if (this.props.isSignedIn && isEmptyObject(this.props.sites)) this.props.getAllSites(this.props.accountUUID);
    }

    setPaymentInformation = () => {
        if (!this.props.mocked && this.props.permissions.includes('settings.paymentInfo')){
            this.props.getPaymentInformation(this.props.accountUUID);
        }
    }

    navigateOnPageLoad() {
        switch (this.authRef) {
            case 'RESET_PASSWORD':
                return <Redirect to='/auth/completepasswordreset'/>;
            case 'COMPLETE_SIGN_UP':
                return <Redirect to='/auth/completesignup'/>;
            case 'ACCOUNT_PORTED':
                const accPortedMessage = `Your Account has been migrated to the new version of our Publisher Portal! 
                                          Please bookmark this page for future use, and login below using your existing information.`;
                this.props.displaySuccessMessage(accPortedMessage);
                return this.props.isSignedIn ? <Redirect to='/dashboard/home'/> : <Redirect to='/auth/signin'/>;
            case 'failed':
            case 'FAILED':
                const googleLoginFailed = `We could not log you in using the Google account you selected; 
                                           please try again using a different account.`;
                this.props.displayErrorMessage(googleLoginFailed);
                return <Redirect to='/auth/signin'/>;
            default:
                return this.props.isSignedIn ? <Redirect to='/dashboard/home'/> : <Redirect to='/auth/signin'/>;
        }
    }

    render() {
        if (this.userIsSignedIn() && this.userDoesNotHavePermissions() && !this.authFail()) {
            return (<LoadState
                        graphicName={`insticator-icon`}
                        graphicText={`Browser with Search Icon`}
                        bodycopy={`Insticator is loading your data.`}
            />)
        }
        return (
            <ConnectedRouter history={history}>
                <div id={`app`} className={`${this.props.classes.app}`}>
                    <Switch>
                        <Route exact path='/' render={() => this.navigateOnPageLoad()} />
                        <Route path='/auth' render={() => (this.props.isSignedIn ? (<Redirect to='/dashboard/home' />) : (<Auth {...this.props} /> ))} />
                        <PrivateRoute authed={this.props.isSignedIn} path='/dashboard' component={Dashboard} /> {/* !!! Nested routes to sections are in the Dashboard component !!! */}
                        <PrivateRoute authed={this.props.isSignedIn} path='/uikit' component={UIKitPage} />
                        <PrivateRoute authed={this.props.isSignedIn} path='/error' component={Error} />
                        <Redirect to='/error/404' />
                    </Switch>
                    {/*<PrivateRoute authed={props.isDataReceived} exact path='/getstarted' component={GetStartedPage} />*/}
                    <Modal /> {/* activated via redux action/state and actual content is passed through there as well */}
                    <MessageHolder /> {/* activated via redux action/state and actual content is passed through there as well */}
                </div>
            </ConnectedRouter>
        );
    }
}


const PrivateRoute = ({ component: Component, authed, ...rest }) => {
    return(
        <Route
            {...rest}
            render={(props) => authed === true
                ? <Component {...props} />
                : <Redirect to={{ pathname: '/auth/signin', state: { from: props.location } }} />}
        />
    )
};


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
// will be utilized in parent component after being infused with app state & actions
export default injectSheet(styles)(App);
